import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Snackbar,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Rating,
  LinearProgress
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  Security as SecurityIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Save as SaveIcon,
  Star as StarIcon,
  Insights as InsightsIcon
} from '@mui/icons-material';
import { fetchJson } from '../utils/fetchJson';
import { deepOrange } from '@mui/material/colors';
import { useTranslation } from 'react-i18next';
import { themeMetadata, PALETTES, DEFAULT_PALETTE_ID, C } from '../themes';
import { showsOwnEvaluations } from '../utils/profileVisibility.generated';

const getInitials = (name) => {
  if (!name) return '?';
  return name.trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();
};

const MyProfilePage = () => {
  const { t, i18n } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPreferences, setSavingPreferences] = useState(false);
  const [savingAppearance, setSavingAppearance] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [settings, setSettings] = useState({
    theme: DEFAULT_PALETTE_ID,
    language: 'en',
    sidebarCollapsed: false
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [ratingsData, setRatingsData] = useState(null);
  const [ratingsLoading, setRatingsLoading] = useState(false);
  const [performance, setPerformance] = useState(null);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const photoInputRef = useRef(null);

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    showCurrentPassword: false,
    showNewPassword: false,
    showConfirmPassword: false
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    return () => {
      if (!photoPreview) return;
      try {
        URL.revokeObjectURL(photoPreview);
      } catch (_) {}
    };
  }, [photoPreview]);

  useEffect(() => {
    if (!profile) return;
    const rn = String(profile?.Role?.name || profile?.role?.name || profile?.role || '').trim().toLowerCase();
    const ineligible = ['student', 'parent', 'systemadmin'].some(r => rn.includes(r));
    if (ineligible || !profile.id) return;
    let cancelled = false;
    (async () => {
      try {
        setRatingsLoading(true);
        const resp = await fetchJson('/api/staff-management/evaluations/ratings-summary/me');
        if (!cancelled) setRatingsData(resp);
      } catch (_) {
        if (!cancelled) setRatingsData(null);
      } finally {
        if (!cancelled) setRatingsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [profile?.id]);

  // Automatic activity-based performance scorecard (hidden for non-staff via isStaff flag)
  useEffect(() => {
    if (!profile) return;
    const rn = String(profile?.Role?.name || profile?.role?.name || profile?.role || '').trim().toLowerCase();
    const ineligible = ['student', 'parent', 'systemadmin'].some(r => rn.includes(r));
    if (ineligible || !profile.id) return;
    let cancelled = false;
    (async () => {
      try {
        setPerformanceLoading(true);
        const resp = await fetchJson('/api/staff-management/performance/me');
        if (!cancelled) setPerformance(resp?.performance || null);
      } catch (_) {
        if (!cancelled) setPerformance(null);
      } finally {
        if (!cancelled) setPerformanceLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [profile?.id]);

  const loadData = async () => {
    try {
      setLoading(true);
      let pRes = null, sRes = null;
      // Load profile with fallback; if both fail, bubble up
      try {
        pRes = await fetchJson('/user-settings/profile');
      } catch (e1) {
        try {
          pRes = await fetchJson('/api/user-settings/profile');
        } catch (e2) {
          throw e2;
        }
      }
      // Load settings with fallback; swallow errors so profile can still render
      try {
        sRes = await fetchJson('/user-settings/settings');
      } catch (e3) {
        try {
          sRes = await fetchJson('/api/user-settings/settings');
        } catch (e4) {
          // ignore settings load failure; profile will still display
        }
      }
      if (pRes?.success) {
        setProfile(pRes.data);
      } else {
        setError(pRes?.message || 'Failed to load profile');
        return;
      }
      if (sRes?.success && sRes.data) setSettings(sRes.data);
    } catch (err) {
      console.error('Error loading profile/settings:', err);
      const msg = (err && err.message) ? String(err.message).slice(0, 200) : 'Failed to load profile';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const onSelectPhoto = (e) => {
    const f = e.target.files && e.target.files[0];
    if (f) {
      setPhotoFile(f);
      try {
        const url = URL.createObjectURL(f);
        setPhotoPreview((prev) => {
          try {
            if (prev) URL.revokeObjectURL(prev);
          } catch (_) {}
          return url;
        });
      } catch (_) {}
    }
  };

  const handlePhotoUpload = async () => {
    if (!photoFile) return;
    try {
      setUploadingPhoto(true);
      const formData = new FormData();
      formData.append('profilePhoto', photoFile);

      let result;
      try {
        result = await fetchJson('/api/user-settings/profile/photo', {
          method: 'POST',
          body: formData
        });
      } catch (e1) {
        result = await fetchJson('/user-settings/profile/photo', {
          method: 'POST',
          body: formData
        });
      }

      if (result?.success && result?.data?.avatarUrl) {
        setProfile((prev) => ({ ...(prev || {}), avatarUrl: result.data.avatarUrl }));
        try {
          window.dispatchEvent(new CustomEvent('app:user:avatar:updated', { detail: { avatarUrl: result.data.avatarUrl } }));
        } catch (_) {}
        setSnackbar({ open: true, message: t('profileSettings.profile.photoUpdatedSuccess') || 'Profile photo updated', severity: 'success' });
        setPhotoFile(null);
        setPhotoPreview('');
      } else {
        throw new Error(result?.message || 'Upload failed');
      }
    } catch (err) {
      setSnackbar({ open: true, message: err?.userMessage || t('profileSettings.errors.photoUploadFailed') || 'Photo upload failed', severity: 'error' });
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleProfileUpdate = async () => {
    try {
      setSavingProfile(true);
      let res;
      try {
        res = await fetchJson('/user-settings/profile', {
          method: 'PUT',
          body: { name: profile?.name || '', phone: profile?.phone || '' }
        });
      } catch (e1) {
        try {
          res = await fetchJson('/api/user-settings/profile', {
            method: 'PUT',
            body: { name: profile?.name || '', phone: profile?.phone || '' }
          });
        } catch (e2) {
          throw e2;
        }
      }
      if (res?.success) {
        setSnackbar({ open: true, message: t('profileSettings.profile.updatedSuccess') || 'Profile updated', severity: 'success' });
      }
    } catch (e) {
      setSnackbar({ open: true, message: t('profileSettings.errors.updateFailed') || 'Update failed', severity: 'error' });
    } finally { setSavingProfile(false); }
  };

  /**
   * APPLY THE PALETTE AT ONCE, then let Save persist it.
   *
   * Setting state alone changed only which row looked selected — the page kept
   * its old colours until somebody pressed Save, so clicking a palette appeared
   * to do nothing at all. Choosing a theme is a visual decision and has to be
   * visible while it is being made; the phone has always applied it on the tap.
   *
   * The event is the one App.js already listens for, so this is the same path
   * saving takes rather than a second way of changing the theme. App.js also
   * mirrors it to localStorage, which means the choice survives a reload even
   * if the person navigates away without saving — and Save is what carries it
   * to their other devices.
   */
  const pickPalette = (id) => {
    setSettings((prev) => ({ ...prev, theme: id }));
    try {
      window.dispatchEvent(new CustomEvent('app:user:settings:updated', { detail: { theme: id } }));
    } catch (_) { /* the Save button still works */ }
  };

  const handleSettingsUpdate = async (section) => {
    try {
      if (section === 'appearance') {
        setSavingAppearance(true);
      } else {
        setSavingPreferences(true);
      }
      let res;
      try {
        res = await fetchJson('/user-settings/settings', { method: 'PUT', body: settings });
      } catch (e1) {
        try {
          res = await fetchJson('/api/user-settings/settings', { method: 'PUT', body: settings });
        } catch (e2) {
          throw e2;
        }
      }
      if (res?.success) {
        if (settings.language && settings.language !== i18n.language) {
          try { i18n.changeLanguage(settings.language); } catch (_) {}
        }
        try { window.dispatchEvent(new CustomEvent('app:user:settings:updated', { detail: { theme: settings.theme, language: settings.language } })); } catch (_) {}
        setSnackbar({ open: true, message: t('profileSettings.settings.updatedSuccess') || 'Settings saved', severity: 'success' });
      }
    } catch (e) {
      setSnackbar({ open: true, message: t('profileSettings.errors.updateFailed') || 'Save failed', severity: 'error' });
    } finally {
      if (section === 'appearance') {
        setSavingAppearance(false);
      } else {
        setSavingPreferences(false);
      }
    }
  };

  const handlePasswordChange = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setSnackbar({ open: true, message: t('profileSettings.password.missing') || 'Fill all password fields', severity: 'error' });
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setSnackbar({ open: true, message: t('profileSettings.password.mismatch') || 'Passwords do not match', severity: 'error' });
      return;
    }
    if (passwordData.newPassword.length < 7) {
      setSnackbar({ open: true, message: t('profileSettings.password.tooShort') || 'Password must be at least 7 characters', severity: 'error' });
      return;
    }
    if (!/[a-zA-Z]/.test(passwordData.newPassword) || !/[0-9]/.test(passwordData.newPassword)) {
      setSnackbar({ open: true, message: t('profileSettings.password.weakPassword') || 'Password must contain at least one letter and one number', severity: 'error' });
      return;
    }
    try {
      setSavingPassword(true);
      let res;
      try {
        res = await fetchJson('/user-settings/profile/password', {
          method: 'PUT',
          body: { currentPassword: passwordData.currentPassword, newPassword: passwordData.newPassword }
        });
      } catch (e1) {
        try {
          res = await fetchJson('/api/user-settings/profile/password', {
            method: 'PUT',
            body: { currentPassword: passwordData.currentPassword, newPassword: passwordData.newPassword }
          });
        } catch (e2) {
          throw e2;
        }
      }
      if (res?.success) {
        setSnackbar({ open: true, message: t('profileSettings.password.changedSuccess') || 'Password changed', severity: 'success' });
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '', showCurrentPassword: false, showNewPassword: false, showConfirmPassword: false });
      }
    } catch (e) {
      setSnackbar({ open: true, message: e?.message || t('profileSettings.errors.passwordChangeFailed') || 'Password change failed', severity: 'error' });
    } finally { setSavingPassword(false); }
  };

  const formatDate = (value, mode = 'date') => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return mode === 'datetime' ? d.toLocaleString() : d.toLocaleDateString();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!profile) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">{t('profileSettings.errors.loadFailed') || 'No profile data available'}</Alert>
      </Box>
    );
  }

  const roleLabel = (profile?.Role?.name) || (profile?.role?.name) || (profile?.role) || 'User';
  const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000').replace(/\/$/, '');
  const avatarSrc = profile.avatarUrl
    ? (profile.avatarUrl.startsWith('http')
        ? profile.avatarUrl
        : `${apiBase}${profile.avatarUrl.startsWith('/') ? '' : '/'}${profile.avatarUrl}`)
    : undefined;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1200, mx: 'auto' }}>

      <Grid container spacing={3}>
        {/* Profile Header */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Avatar
              src={avatarSrc}
              sx={{
                width: 120,
                height: 120,
                mx: 'auto',
                mb: 2,
                bgcolor: profile.avatarUrl ? undefined : deepOrange[500],
                fontSize: '3rem',
                border: '4px solid',
                borderColor: 'primary.main'
              }}
            >
              {!profile.avatarUrl && getInitials(profile.name)}
            </Avatar>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 1 }}>
              <input ref={photoInputRef} id="profile-photo-input" type="file" accept="image/*" style={{ display: 'none' }} onChange={onSelectPhoto} />
              <Button size="small" variant="outlined" onClick={() => photoInputRef.current?.click()}>
                {photoFile ? 'Change Photo' : 'Choose Photo'}
              </Button>
              <Button size="small" variant="contained" disabled={!photoFile || uploadingPhoto} onClick={handlePhotoUpload}>
                {uploadingPhoto ? 'Uploading...' : 'Upload'}
              </Button>
            </Box>
            {photoPreview && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                <Avatar src={photoPreview} sx={{ width: 64, height: 64 }} />
              </Box>
            )}
            
            <Typography variant="h4" gutterBottom>
              {profile.name}
            </Typography>
            
            <Chip 
              label={roleLabel} 
              color="primary" 
              size="large"
              sx={{ mb: 2 }}
            />
            
            <Typography variant="body1" color="textSecondary">
              {profile.Role?.description || profile.role?.description || 'School Management System User'}
            </Typography>
          </Paper>
        </Grid>

        {/* Profile Details */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon color="primary" />
                {t('profileSettings.profile.personalInfo') || 'Personal Information'}
              </Typography>
              
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('profileSettings.profile.fullName') || 'Full Name'}
                    value={profile.name || ''}
                    onChange={(e)=>setProfile(p=>({...p, name: e.target.value}))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('profileSettings.profile.phoneNumber') || 'Phone'}
                    value={profile.phone || ''}
                    onChange={(e)=>setProfile(p=>({...p, phone: e.target.value}))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('profileSettings.profile.email') || 'Email'}
                    value={profile.email || ''}
                    disabled
                    helperText={t('profileSettings.profile.emailCannotChange') || 'Email cannot be changed'}
                  />
                </Grid>
              </Grid>
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="textSecondary">Status</Typography>
                <Chip label={profile.status} color={profile.status === 'active' ? 'success' : 'warning'} size="small" />
              </Box>
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={handleProfileUpdate}
                  disabled={savingProfile}
                  startIcon={savingProfile ? <CircularProgress size={20} /> : <SaveIcon />}
                >
                  {savingProfile
                    ? (t('profileSettings.common.saving') || 'Saving...')
                    : (t('profileSettings.common.saveChanges') || 'Save Changes')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Account Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BusinessIcon color="primary" />
                Account Information
              </Typography>
              
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <CalendarIcon color="action" />
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Member Since
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(profile.createdAt, 'date')}
                    </Typography>
                  </Box>
                </Box>
                
                {profile.lastActive && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <CalendarIcon color="action" />
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Last Active
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(profile.lastActive, 'datetime')}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Preferences */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('profileSettings.tabs.preferences') || 'Preferences'}
              </Typography>
              <FormControl fullWidth margin="normal">
                <InputLabel>{t('profileSettings.preferences.language') || 'Language'}</InputLabel>
                <Select
                  value={settings.language}
                  label={t('profileSettings.preferences.language') || 'Language'}
                  onChange={(e)=>{
                    const lang = e.target.value;
                    setSettings(s=>({...s, language: lang}));
                    if (lang && lang !== i18n.language) {
                      try {
                        i18n.changeLanguage(lang);
                        if (typeof window !== 'undefined' && window.localStorage) {
                          localStorage.setItem('i18nextLng', lang);
                        }
                      } catch (_) {}
                    }
                  }}
                >
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="fr">Français</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                sx={{ mt: 1 }}
                control={<Switch checked={!!settings.sidebarCollapsed} onChange={(e)=>setSettings(s=>({...s, sidebarCollapsed: e.target.checked}))} />}
                label={t('profileSettings.preferences.collapseSidebar') || 'Collapse Sidebar'}
              />
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={() => handleSettingsUpdate('preferences')}
                  disabled={savingPreferences}
                  startIcon={savingPreferences ? <CircularProgress size={20} /> : <SaveIcon />}
                >
                  {savingPreferences
                    ? (t('profileSettings.common.saving') || 'Saving...')
                    : (t('profileSettings.preferences.savePreferences') || 'Save Preferences')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Appearance */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('profileSettings.tabs.appearance') || 'Appearance'}
              </Typography>
              {/* FIVE PALETTES, and each row shows THREE of its colours rather
                  than one. A palette here is a complete scheme — the chrome, the
                  action colour and the accent move together — and a single dot
                  cannot say that; someone picking from five single dots is
                  choosing a highlight colour, which is not what this does.

                  The choice saves with the rest of the appearance settings and
                  is applied the moment it is saved, through the
                  `app:user:settings:updated` event App.js already listens for.
                  The phone reads the same five from the same file. */}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {t('profileSettings.appearance.hint')
                  || 'Pick a palette to see it applied straight away. Save to keep it on your other devices.'}
              </Typography>
              <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                {PALETTES.map((pal) => {
                  const on = (settings.theme || DEFAULT_PALETTE_ID) === pal.id;
                  return (
                    <Grid item xs={12} sm={6} key={pal.id}>
                      <Box
                        role="button"
                        tabIndex={0}
                        onClick={() => pickPalette(pal.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            pickPalette(pal.id);
                          }
                        }}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1.5,
                          p: 1.5, borderRadius: 2, cursor: 'pointer',
                          border: '2px solid',
                          borderColor: on ? pal.mid : 'divider',
                          bgcolor: on ? pal.tint : 'transparent',
                          transition: 'border-color 150ms, background-color 150ms',
                          '&:hover': { borderColor: pal.soft },
                        }}
                      >
                        {/* THE SWATCHES SIT ON THE PALETTE'S OWN GROUND.
                            Three chips floating on the current card is a fair
                            preview of a light scheme and a lie about the dark
                            one — it would show Dark Mode's blue and gold with no
                            hint that the page behind them is near-black, which
                            is the whole of what makes it a different choice. */}
                        <Box
                          sx={{
                            display: 'flex', gap: 0.4, flexShrink: 0,
                            p: 0.6, borderRadius: 1,
                            bgcolor: pal.bg,
                            border: '1px solid', borderColor: pal.border,
                          }}
                        >
                          {[pal.deep, pal.accent, pal.mid].map((c) => (
                            <Box key={c} sx={{ width: 12, height: 28, borderRadius: 0.75, bgcolor: c }} />
                          ))}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={on ? 700 : 600} noWrap>
                            {themeMetadata[pal.id].name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {themeMetadata[pal.id].description}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={() => handleSettingsUpdate('appearance')}
                  disabled={savingAppearance}
                  startIcon={savingAppearance ? <CircularProgress size={20} /> : <SaveIcon />}
                >
                  {savingAppearance
                    ? (t('profileSettings.common.saving') || 'Saving...')
                    : (t('profileSettings.appearance.saveSettings') || 'Save Appearance')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Automatic Performance Scorecard (live, activity-based) */}
        {(performanceLoading || performance?.isStaff) && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InsightsIcon color="primary" /> My Performance
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Auto-calculated from your activity • updates in real time
                  </Typography>
                </Box>

                {performanceLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={36} /></Box>
                ) : (() => {
                  const bandColor = { excellent: 'success', good: 'info', fair: 'warning', needs_improvement: 'error', pending: 'default' }[performance.band] || 'default';
                  const bandLabel = { excellent: 'Excellent', good: 'Good', fair: 'Fair', needs_improvement: 'Needs Improvement', pending: 'Not enough data yet' }[performance.band] || '—';
                  const scoreColor = (s) => s == null ? 'text.secondary' : s >= 80 ? 'success.main' : s >= 60 ? 'primary.main' : s >= 40 ? 'warning.main' : 'error.main';
                  const barColor = (s) => s == null ? 'inherit' : s >= 80 ? 'success' : s >= 60 ? 'primary' : s >= 40 ? 'warning' : 'error';
                  return (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ textAlign: 'center', minWidth: 110 }}>
                          <Typography variant="h2" fontWeight={800} color={scoreColor(performance.overall)}>
                            {performance.overall == null ? '—' : performance.overall}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">/ 100 overall</Typography>
                        </Box>
                        <Box>
                          <Chip label={bandLabel} color={bandColor} sx={{ fontWeight: 700, mb: 0.5 }} />
                          <Typography variant="body2" color="text.secondary">
                            Based on your recorded activity this academic year.
                          </Typography>
                        </Box>
                      </Box>
                      <Divider sx={{ my: 1.5 }} />
                      <Grid container spacing={2}>
                        {(performance.metrics || []).map((m) => (
                          <Grid item xs={12} md={6} key={m.key}>
                            <Box sx={{ mb: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" fontWeight={600}>{m.label}</Typography>
                              <Typography variant="body2" fontWeight={700} color={scoreColor(m.score)}>
                                {m.score == null ? 'No data yet' : `${m.score}%`}
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={typeof m.score === 'number' ? m.score : 0}
                              color={barColor(m.score)}
                              sx={{ height: 8, borderRadius: 4, mb: 0.5 }}
                            />
                            <Typography variant="caption" color="text.secondary">{m.detail}</Typography>
                          </Grid>
                        ))}
                      </Grid>
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Staff Evaluation Summary */}
        {(() => {
          // Shared with the phone. Both clients held the same three-role list
          // and the same bug in it — see packages/core/src/ui/visibility.js.
          const roleName = profile?.Role?.name || profile?.role?.name || profile?.role || '';
          if (!showsOwnEvaluations(roleName)) return null;
          return (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StarIcon color="primary" />
                    My Evaluations
                  </Typography>
                  {ratingsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                      <CircularProgress size={36} />
                    </Box>
                  ) : !ratingsData ? (
                    <Alert severity="info">No evaluation data available.</Alert>
                  ) : ratingsData.eligible === false ? (
                    <Alert severity="info">You are not currently enrolled in the staff evaluation programme.</Alert>
                  ) : (
                    <>
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={4}>
                          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="overline" color="text.secondary">Average Rating</Typography>
                            <Typography variant="h4" fontWeight={800}>
                              {ratingsData?.summary?.averageRating != null ? Number(ratingsData.summary.averageRating).toFixed(1) : '—'}
                            </Typography>
                            <Rating value={Number(ratingsData?.summary?.averageRating || 0)} readOnly precision={0.1} />
                          </Paper>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="overline" color="text.secondary">Total Evaluations</Typography>
                            <Typography variant="h4" fontWeight={800}>{Number(ratingsData?.summary?.evaluationCount || 0)}</Typography>
                            <Typography variant="caption" color="text.secondary">Submitted: {Number(ratingsData?.summary?.submittedCount || 0)}</Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="overline" color="text.secondary">Last Evaluation</Typography>
                            <Typography variant="h6" fontWeight={700}>{ratingsData?.summary?.lastEvaluationDate || '—'}</Typography>
                          </Paper>
                        </Grid>
                      </Grid>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Recent Evaluations</Typography>
                      {!Array.isArray(ratingsData?.evaluations) || ratingsData.evaluations.length === 0 ? (
                        <Alert severity="info">No evaluations recorded yet.</Alert>
                      ) : (
                        <List dense>
                          {ratingsData.evaluations.slice(0, 5).map((ev) => (
                            <ListItem key={ev.id} divider>
                              <ListItemText
                                primary={`${ev.evaluationDate || '—'}${ev.period ? ` • ${ev.period}` : ''}`}
                                secondary={`Status: ${ev.status || '—'} • Rating: ${Number(ev.overallRating || 0).toFixed(1)}`}
                              />
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })()}

        {/* Security */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('profileSettings.tabs.security') || 'Security'}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type={passwordData.showCurrentPassword ? 'text':'password'}
                    label={t('profileSettings.security.currentPassword') || 'Current Password'}
                    value={passwordData.currentPassword}
                    onChange={(e)=>setPasswordData(d=>({...d, currentPassword: e.target.value}))}
                    InputProps={{ endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={()=>setPasswordData(d=>({...d, showCurrentPassword: !d.showCurrentPassword}))}>
                        {passwordData.showCurrentPassword ? <VisibilityOffIcon/> : <VisibilityIcon/>}
                      </IconButton>
                    </InputAdornment>
                  )}}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type={passwordData.showNewPassword ? 'text':'password'}
                    label={t('profileSettings.security.newPassword') || 'New Password'}
                    value={passwordData.newPassword}
                    onChange={(e)=>setPasswordData(d=>({...d, newPassword: e.target.value}))}
                    InputProps={{ endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={()=>setPasswordData(d=>({...d, showNewPassword: !d.showNewPassword}))}>
                        {passwordData.showNewPassword ? <VisibilityOffIcon/> : <VisibilityIcon/>}
                      </IconButton>
                    </InputAdornment>
                  )}}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type={passwordData.showConfirmPassword ? 'text':'password'}
                    label={t('profileSettings.security.confirmPassword') || 'Confirm Password'}
                    value={passwordData.confirmPassword}
                    onChange={(e)=>setPasswordData(d=>({...d, confirmPassword: e.target.value}))}
                    InputProps={{ endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={()=>setPasswordData(d=>({...d, showConfirmPassword: !d.showConfirmPassword}))}>
                        {passwordData.showConfirmPassword ? <VisibilityOffIcon/> : <VisibilityIcon/>}
                      </IconButton>
                    </InputAdornment>
                  )}}
                  />
                </Grid>
              </Grid>
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={handlePasswordChange}
                  disabled={savingPassword || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                  startIcon={savingPassword ? <CircularProgress size={20} /> : <SaveIcon />}
                >
                  {savingPassword
                    ? (t('profileSettings.security.changing') || 'Changing...')
                    : (t('profileSettings.security.changePassword') || 'Change Password')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MyProfilePage;
